import { EmployeeCapabilityValues, type EmployeeCapability } from '../generated/enums';

/**
 * What a member of staff can be given to do, in the words the shop uses (spec Section 24).
 *
 * <p>
 * <strong>The twin of `pages/vendors/capabilities.ts`, and deliberately not shared with it.</strong>
 * The two enums answer the same question — "who can take this alteration?" — and are different
 * catalogues: a vendor can be a `Supplier` and an employee cannot, an employee works the `Counter`
 * and a vendor does not. A single module over both would need a union type whose labels are partial
 * on either side, which is exactly the shape the `Record<Exclude<…>>` guard below exists to refuse.
 * What *is* shared is the wording where the work is the same, so a screen showing both masters in
 * one list — {@link TailorPicker} — does not call the same job two things.
 * </p>
 *
 * <p>
 * <strong>Its own module because the enum it maps is generated.</strong> `EmployeeCapability` is
 * projected from the C# enum, so a fifth capability added on the server would otherwise arrive with
 * no label and the screen would render an empty chip against somebody who does something. Two
 * guards, in that order: the `Record<Exclude<…>>` makes it a **compile error**; and
 * `everyCapabilityHasALabel` covers what the type cannot, since a label could be added as an empty
 * string, satisfy the type, and render nothing.
 * </p>
 */
export const capabilityLabels: Record<Exclude<EmployeeCapability, 'None'>, string> = {
  Tailor: 'Alterations',
  Laundry: 'Cleaning',
  Counter: 'Counter',
  Delivery: 'Delivery',
};

/**
 * One line of explanation each, for the drawer's tick boxes.
 *
 * The label alone does not tell a shop owner that ticking "Alterations" is what puts this person in
 * the list the tailor picker offers beside the workshops down the lane — which is the whole reason
 * the flags exist rather than a job title somebody typed.
 */
export const capabilityHints: Record<Exclude<EmployeeCapability, 'None'>, string> = {
  Tailor: 'Alters and repairs garments in the back room',
  Laundry: 'Cleans and presses in the shop',
  Counter: 'Takes bookings, issues and receives garments',
  Delivery: 'Delivers and collects',
};

/**
 * The capabilities a person can actually be given, in the order a screen should read them.
 *
 * Derived from the generated list rather than written out a second time, and `None` — which means
 * the absence of one — is dropped here in the one place.
 */
export const selectableCapabilities: Exclude<EmployeeCapability, 'None'>[] =
  EmployeeCapabilityValues.filter(
    (value): value is Exclude<EmployeeCapability, 'None'> => value !== 'None',
  );

/**
 * The chips above the list. `undefined` is "all", which is not a capability and deliberately not a
 * value the API is sent.
 */
export const capabilityFilters: { value: EmployeeCapability | undefined; label: string }[] = [
  { value: undefined, label: 'All' },
  ...selectableCapabilities.map((value) => ({ value, label: capabilityLabels[value] })),
];

/**
 * The shop's word for one capability.
 *
 * `None` falls through to itself rather than to a friendlier string. It is the one place this
 * module differs in *meaning* from the vendor's twin: a vendor may not claim it and reaching a
 * screen would be a defect, while an employee legitimately holds no capability at all — the
 * head-office accountant does none of the four things the rental loop hands out. That row carries
 * an **empty list**, though, not a `None` in it, so a `None` arriving here is still wrong and still
 * shows rather than hides.
 */
export function capabilityLabel(capability: EmployeeCapability): string {
  return capability === 'None' ? capability : capabilityLabels[capability];
}

/**
 * Whether every capability the server can send has a word for it here. Exported so the test can ask
 * the question rather than restating the list.
 */
export function everyCapabilityHasALabel(): boolean {
  return selectableCapabilities.every(
    (capability) => Boolean(capabilityLabels[capability]) && Boolean(capabilityHints[capability]),
  );
}
